import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Play,
  Clock,
  Check,
  X,
  RefreshCw,
  ArrowDown,
  Moon as RestDayIcon
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { WorkoutService } from '../lib/workoutService';
import { getUnreadCount as getUnreadTrainerNotificationCount, subscribeToNewNotifications } from '../lib/memberNotificationService';
import { MemberWorkoutPlan, WorkoutSession, WorkoutPlanWithSchedule } from '../types/dataModel';
import { StickyWorkoutCalendar, type CalendarViewState, type StickyWorkoutCalendarHandle } from '../components/StickyWorkoutCalendar';
import { useCachedData } from '../hooks/useCachedData';
import CacheService from '../lib/cacheService';
import { memberDashboardCacheKey, dashboardDataVersionFromPayload } from '../lib/memberDashboardCache';
import { getSessionWorkoutBannerUrl } from '../lib/workoutBannerImage';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { BravosWordmark } from '../components/BravosWordmark';
import { PAGE_CHROME_LOADER_BRAND_GREEN } from '../constants/pageChrome';
import { usePageStatusBar } from '../hooks/usePageStatusBar';
import './MemberDashboardCalendarTheme.css';
import { toLocalDateKey } from '../utils/dateKey';

const WORKOUT_BANNER_TOP_BAR_PX = 56;
const WORKOUT_BANNER_BOTTOM_BAR_PX = 104;
/** Carousel card: total size and bar heights. */
const WORKOUT_BANNER_CAROUSEL_SIZE_PX = 280;
const WORKOUT_BANNER_CAROUSEL_TOP_PX = 48;
const WORKOUT_BANNER_CAROUSEL_BOTTOM_PX = 88;

interface MemberDashboardProps {
  /** When true, welcome and workout banner are in one container; banner has no animation/borders */
  compactLayout?: boolean;
}

/** Shape of cached dashboard data (includes _version for useCachedData getVersionFromData). */
type DashboardCacheData = {
  plans: (MemberWorkoutPlan & { workoutPlan: WorkoutPlanWithSchedule })[];
  active: (MemberWorkoutPlan & { workoutPlan: WorkoutPlanWithSchedule }) | null;
  sessions: (WorkoutSession & { workout?: any })[];
  loadedRange: { start: string; end: string } | null;
  /** Server-wide dashboard version (assignments + any session); never derived from calendar window only. */
  _version: string | null;
};

export const MemberDashboard = ({ compactLayout = false }: MemberDashboardProps) => {
  usePageStatusBar({ background: PAGE_CHROME_LOADER_BRAND_GREEN });

  const { profile, user, markAsLoggedIn } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const prevPathnameRef = useRef<string>('');
  const [memberPlans, setMemberPlans] = useState<(MemberWorkoutPlan & { workoutPlan: WorkoutPlanWithSchedule })[]>([]);
  const [allSessions, setAllSessions] = useState<(WorkoutSession & { workout?: any })[]>([]);
  /** Per assignment: { total, completed } from DB (total shrinks when trainer deletes sessions). */
  const [planBannerStatsByPlanId, setPlanBannerStatsByPlanId] = useState<
    Record<string, { total: number; completed: number }>
  >({});
  /** Range of dates we have sessions for (YYYY-MM-DD). Extended when user navigates calendar beyond this. */
  const [loadedRange, setLoadedRange] = useState<{ start: string; end: string } | null>(null);
  /** Selected calendar date (YYYY-MM-DD, local). Drives workout banner; default today on load. */
  const getLocalDateKey = () => {
    return toLocalDateKey(new Date());
  };
  const [selectedDate, setSelectedDate] = useState<string>(() => getLocalDateKey());
  const [fetchingMore, setFetchingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unreadTrainerNotificationCount, setUnreadTrainerNotificationCount] = useState(0);
  /** Min width for banner CTA so all buttons match "Start Workout" width (set from measured ruler). */
  const [bannerCtaMinWidth, setBannerCtaMinWidth] = useState<number | null>(null);
  const bannerCtaRulerRef = useRef<HTMLSpanElement>(null);
  const bannerCtaRulerContainerRef = useRef<HTMLDivElement>(null);
  const carouselScrollRef = useRef<HTMLDivElement>(null);
  /** Banner shown when Realtime or "first visit after update" signals trainer assigned/updated plan or workouts. Persisted in sessionStorage until dismiss. */
  const [trainerUpdateBanner, setTrainerUpdateBanner] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem('member_dashboard_update_banner');
    } catch {
      return null;
    }
  });

  // Restore calendar view (week only for now); persisted in sessionStorage
  const [initialCalendarState, setInitialCalendarState] = useState<CalendarViewState | null>(() => {
    try {
      const raw = sessionStorage.getItem('memberDashboardCalendarState');
      if (!raw) return null;
      const parsed = JSON.parse(raw) as unknown;
      if (parsed && typeof parsed === 'object' && 'viewMode' in parsed && 'weekDate' in parsed && 'monthDate' in parsed &&
          typeof (parsed as CalendarViewState).viewMode === 'string' && /^\d{4}-\d{2}-\d{2}$/.test((parsed as CalendarViewState).weekDate) && /^\d{4}-\d{2}-\d{2}$/.test((parsed as CalendarViewState).monthDate)) {
        const state = parsed as CalendarViewState;
        return { ...state, viewMode: 'week' as const };
      }
    } catch {
      /* ignore */
    }
    return null;
  });

  const [calendarViewInfo, setCalendarViewInfo] = useState<CalendarViewState | null>(initialCalendarState);

  const persistCalendarState = useCallback((state: CalendarViewState) => {
    const weekOnly = { ...state, viewMode: 'week' as const };
    setCalendarViewInfo(weekOnly);
    try {
      sessionStorage.setItem('memberDashboardCalendarState', JSON.stringify(weekOnly));
    } catch {
      /* ignore */
    }
  }, []);

  const showHeaderToday = useMemo(() => {
    if (!calendarViewInfo) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (calendarViewInfo.viewMode === 'week') {
      const dow = today.getDay();
      const mondayOffset = dow === 0 ? -6 : 1 - dow;
      const todayMonday = new Date(today);
      todayMonday.setDate(today.getDate() + mondayOffset);

      const weekDate = new Date(calendarViewInfo.weekDate + 'T00:00:00');
      const wdow = weekDate.getDay();
      const wMondayOffset = wdow === 0 ? -6 : 1 - wdow;
      const viewMonday = new Date(weekDate);
      viewMonday.setDate(weekDate.getDate() + wMondayOffset);

      return todayMonday.getFullYear() !== viewMonday.getFullYear() ||
             todayMonday.getMonth() !== viewMonday.getMonth() ||
             todayMonday.getDate() !== viewMonday.getDate();
    }

    if (calendarViewInfo.viewMode === 'month') {
      const [viewYear, viewMonth] = calendarViewInfo.monthDate.split('-').map(Number);
      return today.getFullYear() !== viewYear || today.getMonth() !== viewMonth - 1;
    }

    return false;
  }, [calendarViewInfo]);

  // Get member ID
  const memberId = profile?.id || user?.id;

  // Fetch unread trainer-notification count (deferred so main content can paint first). Refetch when Realtime fires and when tab becomes visible.
  const refetchUnreadTrainerNotificationCount = useCallback(() => {
    if (!memberId) return;
    getUnreadTrainerNotificationCount(memberId)
      .then(setUnreadTrainerNotificationCount)
      .catch(() => setUnreadTrainerNotificationCount(0));
  }, [memberId]);

  const DEFER_UNREAD_COUNT_MS = 150;
  useEffect(() => {
    if (!memberId || profile?.role !== 'member') return;
    let cancelled = false;
    const fromNotifications = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('member_from_notifications_page') === '1';
    if (fromNotifications) {
      setUnreadTrainerNotificationCount(0);
      const clearFlagLater = setTimeout(() => {
        try { sessionStorage.removeItem('member_from_notifications_page'); } catch { /* ignore */ }
      }, 2000);
      return () => { cancelled = true; clearTimeout(clearFlagLater); };
    }
    const t = setTimeout(() => {
      getUnreadTrainerNotificationCount(memberId).then((count) => {
        if (!cancelled) setUnreadTrainerNotificationCount(count);
      }).catch(() => { if (!cancelled) setUnreadTrainerNotificationCount(0); });
    }, DEFER_UNREAD_COUNT_MS);
    return () => { cancelled = true; clearTimeout(t); };
  }, [memberId, profile?.role, location.pathname]);

  // When user leaves notifications page, that page marks all as read and fires this event; refetch count so banner/counter clear.
  useEffect(() => {
    if (!memberId || profile?.role !== 'member') return;
    const onMarkedRead = () => refetchUnreadTrainerNotificationCount();
    window.addEventListener('member-notifications-marked-read', onMarkedRead);
    return () => window.removeEventListener('member-notifications-marked-read', onMarkedRead);
  }, [memberId, profile?.role, refetchUnreadTrainerNotificationCount]);

  // When user returns to the tab, refetch unread count (Realtime may not fire when tab was in background).
  useEffect(() => {
    if (!memberId || profile?.role !== 'member') return;
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') refetchUnreadTrainerNotificationCount();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [memberId, profile?.role, refetchUnreadTrainerNotificationCount]);

  const isFirstVisit = profile ? !profile.has_logged_in : false;

  useEffect(() => {
    if (isFirstVisit) {
      markAsLoggedIn();
    }
  }, [isFirstVisit, markAsLoggedIn]);

  // Initial dashboard range: only the week visible on first load (current week, Mon–Sun).
  // Swiping the calendar past loadedRange triggers onDisplayedRangeChange to fetch more light sessions.
  // Dashboard list uses light session shape only; full workout is loaded on demand when user opens a session (e.g. WorkoutSession / WorkoutDetailsPage).
  const getInitialDashboardRange = useCallback(() => {
    const today = new Date();
    const day = today.getDay(); // 0 Sun ... 6 Sat
    const mondayOffset = day === 0 ? -6 : 1 - day;
    const currentMonday = new Date(today);
    currentMonday.setDate(today.getDate() + mondayOffset);
    const start = new Date(currentMonday);
    const end = new Date(currentMonday);
    end.setDate(currentMonday.getDate() + 6); // Sunday of current week
    return {
      start: toLocalDateKey(start),
      end: toLocalDateKey(end),
    };
  }, []);

  // Fetch dashboard data with caching. Uses current loadedRange when set (so Realtime refetch gets fresh data for expanded range); otherwise initial current-week window.
  const fetchDashboardData = useCallback(async () => {
    console.log('🚀 fetchDashboardData called', { user: !!user, memberId });
    if (!user || !memberId) {
      console.log('⚠️ No user or memberId, returning empty data');
      return {
        plans: [],
        active: null,
        sessions: [],
        loadedRange: null,
        _version: null,
      } as DashboardCacheData;
    }

    try {
      const initialRange = getInitialDashboardRange();
      const range = loadedRange ?? initialRange;
      console.log('📥 Fetching plans and sessions in parallel (light):', range);
      const [plans, allSessionsData, _version] = await Promise.all([
        WorkoutService.getMemberWorkoutPlansWithLightDetails(memberId),
        WorkoutService.getMemberWorkoutSessionsLight(memberId, range.start, range.end),
        WorkoutService.getMemberDashboardVersion(memberId),
      ]);
      const active = plans.find(p => p.status === 'active') || plans[0];
      console.log('✅ Plans:', plans.length, 'Sessions:', allSessionsData.length);

      const result = {
        plans,
        active,
        sessions: allSessionsData,
        loadedRange: range,
        _version,
      };
      return result as DashboardCacheData;
    } catch (error) {
      console.error('❌ Error in fetchDashboardData:', error);
      throw error;
    }
  }, [user?.id, memberId, getInitialDashboardRange, loadedRange]);

  const refreshPlanBannerStats = useCallback(async () => {
    if (!memberId) return;
    try {
      const stats = await WorkoutService.getMemberPlanBannerStatsByAssignment(memberId);
      setPlanBannerStatsByPlanId(stats);
    } catch {
      setPlanBannerStatsByPlanId({});
    }
  }, [memberId]);

  // Plan banners: totals + completed from DB (all dates), separate from calendar session window.
  useEffect(() => {
    if (!memberId || profile?.role !== 'member') return;
    void refreshPlanBannerStats();
  }, [memberId, profile?.role, refreshPlanBannerStats]);

  // Cached dashboard data. Same cache is used on refresh and when returning from
  // workout details — we do not invalidate on back navigation, so the UI loads
  // from cache and avoids an extra database round-trip.
  const {
    data: dashboardData,
    loading: dataLoading,
    error: dataError,
    refetch,
    refetchInBackground,
    isStale,
  } = useCachedData<DashboardCacheData>({
    cacheKey: memberId ? memberDashboardCacheKey(memberId) : 'dashboard_v4',
    fetchFn: fetchDashboardData,
    // 24h backstop. Lightweight version avoids redundant full fetches on tab focus / version-first load.
    // Realtime + notification handlers call refetch() (forced refresh), which bypasses that version gate.
    ttl: 24 * 60 * 60 * 1000,
    staleWhileRevalidate: true,
    enabled: !!user && !!memberId,
    refetchOnVisible: true,
    refetchOnVisibleThrottleMs: 30 * 1000,
    checkForUpdates: memberId ? () => WorkoutService.getMemberDashboardVersion(memberId) : undefined,
    getVersionFromData: (d) => dashboardDataVersionFromPayload(d),
  });

  // When member lands on Home again (e.g. from workout details): background fetch always runs (not the visibility version shortcut).
  const prevPathnameForDashboardRefetchRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!memberId || profile?.role !== 'member') return;
    const prev = prevPathnameForDashboardRefetchRef.current;
    const current = location.pathname;
    if (current === '/member-dashboard' && prev != null && prev !== '/member-dashboard') {
      void refetchInBackground();
    }
    prevPathnameForDashboardRefetchRef.current = current;
  }, [location.pathname, memberId, profile?.role, refetchInBackground]);

  // Primary: Realtime → refetch() forces a full dashboard fetch (ignores version check in useCachedData).
  // Backup: member_notifications INSERT (see database_member_notifications*.sql). Same refetch().
  useEffect(() => {
    if (!memberId || profile?.role !== 'member' || !refetch) return;
    const dashSub = WorkoutService.subscribeToMemberDashboardChanges(memberId, () => {
      refetch();
      void refreshPlanBannerStats();
      getUnreadTrainerNotificationCount(memberId).then(setUnreadTrainerNotificationCount).catch(() => {});
      window.dispatchEvent(new CustomEvent('member-notification-count-changed'));
    });
    const notifSub = subscribeToNewNotifications(memberId, (detail) => {
      refetchUnreadTrainerNotificationCount();
      const type = detail?.type ?? null;
      if (
        type === 'workout_removed' ||
        type === 'workout_plan_removed' ||
        type === 'workouts_added' ||
        type === 'workout_updated' ||
        type === 'workout_plan_assigned' ||
        type === 'workout_plan_updated'
      ) {
        refetch();
        void refreshPlanBannerStats();
        window.dispatchEvent(new CustomEvent('member-notification-count-changed'));
      }
    });
    return () => {
      dashSub.unsubscribe();
      notifSub.unsubscribe();
    };
  }, [memberId, profile?.role, refetch, refetchUnreadTrainerNotificationCount, refreshPlanBannerStats]);

  // Update state when cached data is available
  useEffect(() => {
    if (dashboardData) {
      setMemberPlans(dashboardData.plans ?? []);
      setAllSessions(dashboardData.sessions as (WorkoutSession & { workout?: any })[]);
      setLoadedRange(dashboardData.loadedRange ?? null);
    }
  }, [dashboardData]);

  /** Active assignments first, then by start date (newest first). */
  const sortedMemberPlans = useMemo(() => {
    return [...memberPlans].sort((a, b) => {
      const pri = (s: string) => (s === 'active' ? 0 : s === 'paused' ? 1 : 2);
      const d = pri(a.status) - pri(b.status);
      if (d !== 0) return d;
      return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
    });
  }, [memberPlans]);

  const hasWorkoutContext = memberPlans.length > 0 || allSessions.length > 0;


  // When user swipes to a week outside loaded range, fetch that week. We fetch the full displayed range (start..end)
  // so the visible week is fully loaded (previously we only fetched a fixed 2 weeks ahead, so e.g. week of April 24 was missed).
  // Use effectiveLoaded (loadedRange ?? initial) so we still trigger a fetch if user swiped before first load completed.
  const onDisplayedRangeChange = useCallback(
    async (start: string, end: string) => {
      if (!memberId || fetchingMore) return;
      const initialRange = getInitialDashboardRange();
      const effectiveLoaded = loadedRange ?? initialRange;
      const needPast = end < effectiveLoaded.start;
      const needFuture = start > effectiveLoaded.end;
      if (!needPast && !needFuture) return;

      setFetchingMore(true);
      try {
        if (needPast) {
          const rangeEnd = new Date(effectiveLoaded.start);
          rangeEnd.setDate(rangeEnd.getDate() - 1);
          const fetchStart = new Date(start); // displayed range start so we cover the week/month user is viewing
          const fromDate = toLocalDateKey(fetchStart);
          const toDate = toLocalDateKey(rangeEnd);
          const more = await WorkoutService.getMemberWorkoutSessionsLight(memberId, fromDate, toDate);
          setAllSessions((prev) => {
            const byId = new Map(prev.map((s) => [s.id, s]));
            (more as (WorkoutSession & { workout?: any })[]).forEach((s) => byId.set(s.id, s));
            return Array.from(byId.values()).sort(
              (a, b) => a.scheduledDate.localeCompare(b.scheduledDate)
            );
          });
          setLoadedRange((r) => (r ? { start: fromDate, end: r.end } : { start: fromDate, end: initialRange.end }));
        } else {
          const rangeStart = new Date(effectiveLoaded.end);
          rangeStart.setDate(rangeStart.getDate() + 1);
          const fetchEnd = new Date(end); // displayed range end so we cover the week/month user is viewing
          const fromDate = toLocalDateKey(rangeStart);
          const toDate = toLocalDateKey(fetchEnd);
          const more = await WorkoutService.getMemberWorkoutSessionsLight(memberId, fromDate, toDate);
          setAllSessions((prev) => {
            const byId = new Map(prev.map((s) => [s.id, s]));
            (more as (WorkoutSession & { workout?: any })[]).forEach((s) => byId.set(s.id, s));
            return Array.from(byId.values()).sort(
              (a, b) => a.scheduledDate.localeCompare(b.scheduledDate)
            );
          });
          setLoadedRange((r) => (r ? { start: r.start, end: toDate } : { start: initialRange.start, end: toDate }));
        }
      } catch (err) {
        console.error('Error fetching more sessions:', err);
      } finally {
        setFetchingMore(false);
      }
    },
    [memberId, loadedRange, fetchingMore, getInitialDashboardRange]
  );

  // Only show loading when we have no data and are fetching (avoids flash when returning from workout-details with cache)
  const loading = !dashboardData && dataLoading;

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('dashboard-loading', { detail: loading }));
    return () => {
      window.dispatchEvent(new CustomEvent('dashboard-loading', { detail: false }));
    };
  }, [loading]);

  // Track pathname for other logic (e.g. future use)
  useEffect(() => {
    prevPathnameRef.current = location.pathname;
  }, [location.pathname]);

  // Set error state
  useEffect(() => {
    setError(dataError?.message || null);
  }, [dataError]);

  // Refetch on status update
  const handleStatusUpdate = useCallback(() => {
    // Invalidate cache and refetch
    refetch();
  }, [refetch]);

  // --- Pull-to-refresh (touch events) ---
  const PULL_THRESHOLD = 50;
  /** Below this, the arrow is hidden (opacity 0) but the white bar still had height — caused a stuck-looking sliver. */
  const PTR_SHOW_CHROME_MIN = 10;
  const PULL_MAX = 140;
  /** If the dashboard fetch never settles (network hang), force-reset the spinner. */
  const PTR_REFRESH_TIMEOUT_MS = 28_000;
  /** Keep spinner visible long enough that a fast cache hit does not look like a glitch. */
  const PTR_MIN_SPINNER_MS = 2000;
  const mainRef = useRef<HTMLElement>(null);
  const calendarRef = useRef<StickyWorkoutCalendarHandle>(null);
  const calendarWrapperRef = useRef<HTMLDivElement>(null);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const isRefreshingRef = useRef(false);
  useEffect(() => {
    isRefreshingRef.current = isRefreshing;
  }, [isRefreshing]);
  const touchStartY = useRef(0);
  const touchStartX = useRef(0);
  const isPulling = useRef(false);
  const latestPullDistance = useRef(0);
  const touchStartedOnToggle = useRef(false);
  const touchStartedInCalendar = useRef(false);
  /** Gesture began on horizontal workout carousel (multi-session day). */
  const touchStartedInWorkoutCarouselRef = useRef(false);
  /** Gesture began on workout banner card (single or carousel item). */
  const touchStartedOnWorkoutBannerRef = useRef(false);
  /** Element where the touch started — used for banner tap navigation. */
  const touchStartTargetRef = useRef<HTMLElement | null>(null);
  const rawTouchDy = useRef(0);
  const ptrRefreshSafetyTimerRef = useRef<number | null>(null);
  const ptrMinSpinnerTimerRef = useRef<number | null>(null);
  const ptrRefreshStartedAtRef = useRef(0);
  const ptrPullRafRef = useRef<number | null>(null);
  const ptrPendingPullRef = useRef(0);
  const pullDistanceRef = useRef(0);
  const CALENDAR_PULL_THRESHOLD = 30;

  const cancelPullDistanceRaf = useCallback(() => {
    if (ptrPullRafRef.current != null) {
      cancelAnimationFrame(ptrPullRafRef.current);
      ptrPullRafRef.current = null;
    }
  }, []);

  /** Batch pull UI updates to one frame to cut layout thrash (banner “flicker”). */
  const schedulePullDistance = useCallback(
    (d: number) => {
      ptrPendingPullRef.current = d;
      latestPullDistance.current = d;
      if (ptrPullRafRef.current == null) {
        ptrPullRafRef.current = requestAnimationFrame(() => {
          ptrPullRafRef.current = null;
          setPullDistance(ptrPendingPullRef.current);
        });
      }
    },
    []
  );

  const flushPullDistanceRaf = useCallback(() => {
    cancelPullDistanceRaf();
    const d = ptrPendingPullRef.current;
    latestPullDistance.current = d;
    setPullDistance(d);
  }, [cancelPullDistanceRaf]);

  /** Native document listeners call these; keep refs so the effect does not re-subscribe every render. */
  const schedulePullDistanceRef = useRef(schedulePullDistance);
  schedulePullDistanceRef.current = schedulePullDistance;
  const cancelPullDistanceRafRef = useRef(cancelPullDistanceRaf);
  cancelPullDistanceRafRef.current = cancelPullDistanceRaf;

  useEffect(() => {
    pullDistanceRef.current = pullDistance;
  }, [pullDistance]);

  const clearPtrRefreshSafetyTimer = useCallback(() => {
    if (ptrRefreshSafetyTimerRef.current != null) {
      clearTimeout(ptrRefreshSafetyTimerRef.current);
      ptrRefreshSafetyTimerRef.current = null;
    }
  }, []);

  const clearPtrMinSpinnerTimer = useCallback(() => {
    if (ptrMinSpinnerTimerRef.current != null) {
      clearTimeout(ptrMinSpinnerTimerRef.current);
      ptrMinSpinnerTimerRef.current = null;
    }
  }, []);

  const endPullRefreshUi = useCallback(() => {
    clearPtrRefreshSafetyTimer();
    clearPtrMinSpinnerTimer();
    cancelPullDistanceRaf();
    ptrPendingPullRef.current = 0;
    setIsRefreshing(false);
    setPullDistance(0);
    latestPullDistance.current = 0;
  }, [clearPtrRefreshSafetyTimer, clearPtrMinSpinnerTimer, cancelPullDistanceRaf]);

  useEffect(() => {
    return () => {
      clearPtrRefreshSafetyTimer();
      clearPtrMinSpinnerTimer();
    };
  }, [clearPtrRefreshSafetyTimer, clearPtrMinSpinnerTimer]);

  const resetPullIndicatorOnly = useCallback(() => {
    cancelPullDistanceRaf();
    ptrPendingPullRef.current = 0;
    latestPullDistance.current = 0;
    setPullDistance(0);
  }, [cancelPullDistanceRaf]);

  const handleTouchEnd = useCallback(() => {
    if (isRefreshing) return;

    flushPullDistanceRaf();

    // Touchend without a matching main touchstart (e.g. gesture ended outside) — clear any peek height.
    if (!isPulling.current) {
      if (latestPullDistance.current > 0 || pullDistanceRef.current > 0) {
        resetPullIndicatorOnly();
      }
      touchStartedOnToggle.current = false;
      touchStartedInCalendar.current = false;
      touchStartedInWorkoutCarouselRef.current = false;
      touchStartedOnWorkoutBannerRef.current = false;
      rawTouchDy.current = 0;
      return;
    }

    isPulling.current = false;

    // Arrow toggle: swipe down on arrow in weekly = expand, swipe up on arrow in monthly = collapse
    if (touchStartedOnToggle.current && calendarRef.current) {
      const dy = rawTouchDy.current;
      if (calendarRef.current.viewMode === 'week' && dy >= CALENDAR_PULL_THRESHOLD) {
        calendarRef.current.toggleViewMode();
        touchStartedOnToggle.current = false;
        touchStartedInCalendar.current = false;
        touchStartedInWorkoutCarouselRef.current = false;
        touchStartedOnWorkoutBannerRef.current = false;
        rawTouchDy.current = 0;
        resetPullIndicatorOnly();
        return;
      }
      if (calendarRef.current.viewMode === 'month' && dy <= -CALENDAR_PULL_THRESHOLD) {
        calendarRef.current.toggleViewMode();
        touchStartedOnToggle.current = false;
        touchStartedInCalendar.current = false;
        touchStartedInWorkoutCarouselRef.current = false;
        touchStartedOnWorkoutBannerRef.current = false;
        rawTouchDy.current = 0;
        resetPullIndicatorOnly();
        return;
      }
    }

    // Calendar-started gesture: ignore unless the user actually pulled past the release threshold.
    if (touchStartedInCalendar.current && latestPullDistance.current < PULL_THRESHOLD) {
      touchStartedOnToggle.current = false;
      touchStartedInCalendar.current = false;
      touchStartedInWorkoutCarouselRef.current = false;
      touchStartedOnWorkoutBannerRef.current = false;
      rawTouchDy.current = 0;
      resetPullIndicatorOnly();
      return;
    }

    const dist = latestPullDistance.current;
    if (dist >= PULL_THRESHOLD) {
      ptrRefreshStartedAtRef.current = Date.now();
      setIsRefreshing(true);
      setPullDistance(50);
      clearPtrRefreshSafetyTimer();
      clearPtrMinSpinnerTimer();
      ptrRefreshSafetyTimerRef.current = window.setTimeout(() => {
        ptrRefreshSafetyTimerRef.current = null;
        void refreshPlanBannerStats();
        endPullRefreshUi();
      }, PTR_REFRESH_TIMEOUT_MS);
      Promise.resolve(refetch())
        .catch(() => {
          /* refetch surfaces via dataError */
        })
        .finally(() => {
          void refreshPlanBannerStats();
          clearPtrRefreshSafetyTimer();
          const elapsed = Date.now() - ptrRefreshStartedAtRef.current;
          const remaining = Math.max(0, PTR_MIN_SPINNER_MS - elapsed);
          clearPtrMinSpinnerTimer();
          ptrMinSpinnerTimerRef.current = window.setTimeout(() => {
            ptrMinSpinnerTimerRef.current = null;
            endPullRefreshUi();
          }, remaining);
        });
    } else {
      resetPullIndicatorOnly();
    }
    touchStartedOnToggle.current = false;
    touchStartedInCalendar.current = false;
    touchStartedInWorkoutCarouselRef.current = false;
    touchStartedOnWorkoutBannerRef.current = false;
    rawTouchDy.current = 0;
  }, [
    isRefreshing,
    refetch,
    refreshPlanBannerStats,
    clearPtrRefreshSafetyTimer,
    clearPtrMinSpinnerTimer,
    endPullRefreshUi,
    resetPullIndicatorOnly,
    flushPullDistanceRaf,
  ]);

  /** Browsers fire touchcancel (not touchend) when the OS takes the gesture or the touch is interrupted — without this, pull UI can stay stuck. */
  const handleTouchCancel = useCallback(() => {
    isPulling.current = false;
    touchStartedOnToggle.current = false;
    touchStartedInCalendar.current = false;
    touchStartedInWorkoutCarouselRef.current = false;
    touchStartedOnWorkoutBannerRef.current = false;
    rawTouchDy.current = 0;
    if (!isRefreshing) {
      resetPullIndicatorOnly();
    } else {
      latestPullDistance.current = 0;
    }
  }, [isRefreshing, resetPullIndicatorOnly]);

  const handleTouchEndRef = useRef(handleTouchEnd);
  handleTouchEndRef.current = handleTouchEnd;
  const handleTouchCancelRef = useRef(handleTouchCancel);
  handleTouchCancelRef.current = handleTouchCancel;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  // PTR on document (capture): touchstart/move/end/cancel — avoids React delegation + iOS touchmove gaps.
  useEffect(() => {
    if (loading) return;
    const mainEl = mainRef.current;
    if (!mainEl) return;

    const CALENDAR_PTR_VERTICAL_SLOP = 14;

    const onTouchStart = (e: TouchEvent) => {
      if (isRefreshingRef.current) return;
      const t = e.touches[0];
      if (!t) return;
      if (!mainEl.contains(e.target as Node)) return;
      if (mainEl.scrollTop > 1) {
        isPulling.current = false;
        touchStartedOnToggle.current = false;
        touchStartedInCalendar.current = false;
        touchStartedInWorkoutCarouselRef.current = false;
        touchStartedOnWorkoutBannerRef.current = false;
        return;
      }
      const target = e.target as HTMLElement;
      touchStartY.current = t.clientY;
      touchStartX.current = t.clientX;
      isPulling.current = true;
      rawTouchDy.current = 0;
      touchStartTargetRef.current = target;
      touchStartedOnToggle.current = !!target.closest?.('[data-calendar-toggle]');
      touchStartedInCalendar.current = !!target.closest?.('[data-calendar-container]');
      touchStartedInWorkoutCarouselRef.current = !!target.closest?.('.member-dashboard-workout-carousel');
      touchStartedOnWorkoutBannerRef.current = !!target.closest?.('[data-member-dashboard-workout-banner]');
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!isPulling.current || isRefreshingRef.current) return;
      const touch = e.touches[0];
      if (!touch) return;
      if (!mainEl.contains(e.target as Node)) return;

      const dy = touch.clientY - touchStartY.current;
      rawTouchDy.current = dy;

      if (touchStartedOnToggle.current) {
        const isToggleSwipe =
          (calendarRef.current?.viewMode === 'week' && dy > 10) ||
          (calendarRef.current?.viewMode === 'month' && dy < -10);
        if (isToggleSwipe) e.preventDefault();
        return;
      }

      const atTop = mainEl.scrollTop <= 1;
      if (touchStartedInCalendar.current && (!atTop || dy <= CALENDAR_PTR_VERTICAL_SLOP)) {
        /* calendar: wait for clear downward intent */
      } else if (dy > 0) {
        const dampened = Math.min(dy * 0.55, PULL_MAX);
        schedulePullDistanceRef.current(dampened);
      } else {
        cancelPullDistanceRafRef.current();
        ptrPendingPullRef.current = 0;
        latestPullDistance.current = 0;
        setPullDistance(0);
      }

      const dx = touch.clientX - touchStartX.current;
      const startedInCarousel = touchStartedInWorkoutCarouselRef.current;
      const horizontalIntent = startedInCarousel
        ? Math.abs(dx) > Math.abs(dy) + 6 && Math.abs(dx) > 14
        : Math.abs(dx) > Math.abs(dy) + 14;

      const onBanner = touchStartedOnWorkoutBannerRef.current;
      if (onBanner && startedInCarousel && horizontalIntent) {
        return;
      }

      const verticalPullSlop = startedInCarousel ? 50 : onBanner ? 0 : 12;
      if (atTop && dy > verticalPullSlop && !horizontalIntent) {
        e.preventDefault();
      }
    };

    const BANNER_TAP_MAX_PX = 12;
    const onTouchEnd = (e: TouchEvent) => {
      // Banner tap → navigate: if touch started on workout banner and finger barely moved, it's a tap.
      if (touchStartedOnWorkoutBannerRef.current && !isRefreshingRef.current) {
        const ct = e.changedTouches?.[0];
        if (ct) {
          const dx = ct.clientX - touchStartX.current;
          const dy = ct.clientY - touchStartY.current;
          if (Math.hypot(dx, dy) < BANNER_TAP_MAX_PX) {
            const banner = touchStartTargetRef.current?.closest?.('[data-member-dashboard-workout-banner]') as HTMLElement | null;
            const sessionId = banner?.dataset.workoutSessionId;
            if (sessionId) {
              touchStartTargetRef.current = null;
              // Reset pull state before navigating
              isPulling.current = false;
              touchStartedOnWorkoutBannerRef.current = false;
              navigateRef.current(`/workout-details/${sessionId}`);
              return;
            }
          }
        }
      }
      handleTouchEndRef.current();
    };
    const onTouchCancel = () => {
      touchStartTargetRef.current = null;
      handleTouchCancelRef.current();
    };

    document.addEventListener('touchstart', onTouchStart, { capture: true, passive: true });
    document.addEventListener('touchmove', onTouchMove, { capture: true, passive: false });
    document.addEventListener('touchend', onTouchEnd, { capture: true, passive: true });
    document.addEventListener('touchcancel', onTouchCancel, { capture: true, passive: true });
    return () => {
      document.removeEventListener('touchstart', onTouchStart, { capture: true });
      document.removeEventListener('touchmove', onTouchMove, { capture: true });
      document.removeEventListener('touchend', onTouchEnd, { capture: true });
      document.removeEventListener('touchcancel', onTouchCancel, { capture: true });
    };
  }, [loading]);

  /** Scrolling away from the top drops any half-pulled strip (orphaned pullDistance). */
  useEffect(() => {
    if (loading) return;
    const el = mainRef.current;
    if (!el) return;
    const onScroll = () => {
      if (isRefreshing) return;
      if (pullDistanceRef.current <= 0) return;
      if (el.scrollTop > 2) {
        isPulling.current = false;
        latestPullDistance.current = 0;
        setPullDistance(0);
      }
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [isRefreshing, loading]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'hidden' || isRefreshing) return;
      isPulling.current = false;
      resetPullIndicatorOnly();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [isRefreshing, resetPullIndicatorOnly]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    return 'bg-theme-muted dark:bg-white/10 text-theme-text dark:text-theme-off-white/80';
  };

  /** Action button label by status */
  const getWorkoutActionLabel = (status: string) =>
    status === 'in_progress' ? 'Continue' : status === 'completed' || status === 'skipped' ? 'See Details' : 'Start Workout';

  /** Inline status icon (same row as "WORKOUT XXX" label). Use onDark when card has image bg. */
  const getWorkoutStatusInlineIcon = (status: string, onDark?: boolean) => {
    const iconClass = onDark ? 'w-3.5 h-3.5 flex-shrink-0 text-white' : 'w-3.5 h-3.5 flex-shrink-0 text-theme-text/70 dark:text-theme-off-white/70';
    switch (status) {
      case 'completed': return <Check className={iconClass} strokeWidth={2.5} />;
      case 'skipped': return <X className={iconClass} strokeWidth={2.5} />;
      case 'in_progress': return <Play className={`${iconClass} fill-current`} strokeWidth={2} />;
      default: return <Clock className={iconClass} />;
    }
  };

  /** Pill button: theme green (var(--action-primary)) full opacity, no hover (mobile). See Details = grey pill. */
  const getWorkoutButtonClass = (status: string, _compact?: boolean) => {
    const base = 'inline-block mt-4 text-xs font-normal text-left self-start rounded-full px-3 py-1.5 member-dashboard-banner-cta';
    const isPrimary = status === 'scheduled' || status === 'in_progress';
    return isPrimary
      ? `${base} bg-primary-400 text-gray-800 dark:text-gray-900`
      : `${base} bg-gray-200 dark:bg-white/15 text-gray-800 dark:text-theme-off-white`;
  };

  /** Mesh gradient background: white with visible dominant color (lower white opacity, more color mesh) */
  const getWorkoutCardMeshStyle = (status: string): React.CSSProperties => {
    const mesh = (r: number, g: number, b: number) => ({
      backgroundImage: [
        `radial-gradient(ellipse 140% 140% at 50% 50%, rgba(255,255,255,0.7) 0%, rgba(255,255,255,0.5) 40%, rgba(255,255,255,0.25) 65%, transparent 85%)`,
        `radial-gradient(ellipse 100% 100% at 0% 0%, rgba(255,255,255,0.55) 0%, transparent 55%)`,
        `radial-gradient(ellipse 90% 90% at 100% 100%, rgba(255,255,255,0.5) 0%, transparent 50%)`,
        `radial-gradient(ellipse 110% 110% at 0% 0%, rgba(${r},${g},${b},0.45) 0%, rgba(${r},${g},${b},0.2) 40%, transparent 65%)`,
        `radial-gradient(ellipse 85% 85% at 100% 70%, rgba(${r},${g},${b},0.38) 0%, rgba(${r},${g},${b},0.12) 50%, transparent 65%)`,
        `radial-gradient(ellipse 90% 90% at 50% 50%, rgba(${r},${g},${b},0.28) 0%, transparent 55%)`,
        `radial-gradient(ellipse 70% 70% at 80% 20%, rgba(${r},${g},${b},0.22) 0%, transparent 55%)`,
      ].join(', '),
      backgroundColor: `rgba(255,255,255,0.5)`,
    });
    switch (status) {
      case 'completed': return mesh(179, 231, 131);   // var(--action-primary-pressed)
      case 'scheduled': return mesh(90, 143, 246); // #5a8ff6
      case 'in_progress': return mesh(230, 126, 34); // #e67e22
      case 'skipped': return mesh(235, 63, 52);    // #EB3F34
      default: return {
        backgroundImage: [
          'radial-gradient(ellipse 140% 140% at 50% 50%, rgba(255,255,255,0.7) 0%, rgba(255,255,255,0.45) 45%, transparent 75%)',
          'radial-gradient(ellipse 90% 90% at 50% 50%, rgba(255,255,255,0.4) 0%, transparent 55%)',
        ].join(', '),
        backgroundColor: 'rgba(255,255,255,0.5)',
      };
    }
  };

  // Handle date click from calendar — select date only; banner shows that day's workouts (or empty state)
  const handleDateClick = (date: string, _sessions: (WorkoutSession & { workout?: any })[]) => {
    setSelectedDate(date);
  };

  // Get today's workout, completed workout, and next workout
  const getTodayAndNextWorkout = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayKey = toLocalDateKey(today);
    
    // Find today's active workout (scheduled or in progress)
    const todayWorkout = allSessions.find(session => {
      const sessionKey = session.scheduledDate.split('T')[0];
      return sessionKey === todayKey &&
             (session.status === 'scheduled' || session.status === 'in_progress');
    });

    // Find today's completed workout
    const todayCompletedWorkout = allSessions.find(session => {
      const sessionKey = session.scheduledDate.split('T')[0];
      return sessionKey === todayKey &&
             session.status === 'completed';
    });

    // Find today's skipped workout
    const todaySkippedWorkout = allSessions.find(session => {
      const sessionKey = session.scheduledDate.split('T')[0];
      return sessionKey === todayKey &&
             session.status === 'skipped';
    });

    // Find next upcoming workout
    const upcomingWorkouts = allSessions
      .filter(session => {
        const sessionDateKey = session.scheduledDate.split('T')[0];
        return sessionDateKey > todayKey && 
               (session.status === 'scheduled' || session.status === 'in_progress');
      })
      .sort((a, b) => {
        return a.scheduledDate.localeCompare(b.scheduledDate);
      });

    const nextWorkout = upcomingWorkouts[0] || null;

    return { todayWorkout, todayCompletedWorkout, todaySkippedWorkout, nextWorkout };
  };

  const { todayWorkout, todayCompletedWorkout, todaySkippedWorkout, nextWorkout } = getTodayAndNextWorkout();

  // Session status display order: scheduled → in_progress → completed → skipped (others last)
  const SESSION_STATUS_ORDER: Record<string, number> = { scheduled: 0, in_progress: 1, completed: 2, skipped: 3 };

  // Sessions for the selected calendar date (drives banner content), sorted by status order
  const selectedDateSessions = useMemo(() => {
    const forDate = allSessions.filter(s => s.scheduledDate.split('T')[0] === selectedDate);
    return [...forDate].sort((a, b) => (SESSION_STATUS_ORDER[a.status] ?? 99) - (SESSION_STATUS_ORDER[b.status] ?? 99));
  }, [allSessions, selectedDate]);

  // Reset carousel scroll to start when user selects another day
  useEffect(() => {
    if (carouselScrollRef.current) {
      carouselScrollRef.current.scrollLeft = 0;
    }
  }, [selectedDate]);

  // Banner gradient class from selected date's sessions (completed > scheduled > skipped > none)
  const selectedDateGradientClass = useMemo(() => {
    if (memberPlans.length === 0 && allSessions.length === 0) return 'member-dashboard--no-plan';
    if (selectedDateSessions.length === 0) return 'member-dashboard--no-workout';
    const hasCompleted = selectedDateSessions.some(s => s.status === 'completed');
    const hasScheduled = selectedDateSessions.some(s => s.status === 'scheduled' || s.status === 'in_progress');
    const hasSkipped = selectedDateSessions.some(s => s.status === 'skipped');
    if (hasCompleted) return '';
    if (hasScheduled) return 'member-dashboard--scheduled';
    if (hasSkipped) return 'member-dashboard--skipped';
    return 'member-dashboard--no-workout';
  }, [memberPlans.length, allSessions.length, selectedDateSessions]);

  // Measure "Start Workout" width so banner CTA min-width matches (no jump between labels)
  const measureBannerCtaWidth = useCallback(() => {
    const span = bannerCtaRulerRef.current;
    if (!span) return;
    const w = span.getBoundingClientRect().width;
    if (w > 0) setBannerCtaMinWidth(Math.ceil(w));
  }, []);
  useEffect(() => {
    measureBannerCtaWidth();
    const container = bannerCtaRulerContainerRef.current;
    if (!container) return;
    const ro = new ResizeObserver(measureBannerCtaWidth);
    ro.observe(container);
    return () => ro.disconnect();
  }, [measureBannerCtaWidth, selectedDateSessions.length]);

  /** Resolve workout display name from session (handles camelCase and snake_case from API/cache) */
  const getWorkoutDisplayName = (session: WorkoutSession & { workout?: any }) => {
    const w = session.workout;
    const name = (w && typeof w.name === 'string' && w.name.trim() !== '')
      ? w.name.trim()
      : (w && (w as any).name)
      ? String((w as any).name).trim()
      : (session as any).workout_name
      ? String((session as any).workout_name).trim()
      : '';
    return name || 'Workout';
  };

  /** Format selected date for display in banner (e.g. "Fri, Mar 13" or "Today, Mar 14") */
  const formatSelectedDateLabel = (d: string) => {
    const date = new Date(d + 'T00:00:00');
    const todayKey = toLocalDateKey(new Date());
    const monthDay = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    if (d === todayKey) return `Today, ${monthDay}`;
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  };

  // Format next workout date message
  const getNextWorkoutMessage = () => {
    if (!nextWorkout) return null;
    
    const nextDate = new Date(nextWorkout.scheduledDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    
    const nextDateKey = toLocalDateKey(nextDate);
    const tomorrowKey = toLocalDateKey(tomorrow);
    
    if (nextDateKey === tomorrowKey) {
      return 'tomorrow';
    } else {
      const daysDiff = Math.ceil((nextDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      if (daysDiff === 2) {
        return 'in 2 days';
      } else if (daysDiff <= 7) {
        return `in ${daysDiff} days`;
      } else {
        return `on ${nextDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}`;
      }
    }
  };

  // Removed quickStats - will revamp design from scratch

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div 
      className="member-dashboard overflow-hidden flex flex-col"
      style={{
        WebkitOverflowScrolling: 'touch',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      }}
    >
      <div className="member-dashboard-bg flex-1 min-h-0 flex flex-col relative">
      {/* Scrollable content – header and calendar scroll with the page */}
      {/* PTR: all touch phases on document (see useEffect after handleTouchEnd/Cancel). */}
      <main 
        ref={mainRef}
        className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden flex flex-col scrollbar-hide pb-bottom-nav"
        style={{ overscrollBehavior: 'none' }}
      >
      {/* Pull-to-refresh: omit sub-PTR_SHOW_CHROME_MIN height so we never show a white bar with a hidden arrow. */}
      {(pullDistance > PTR_SHOW_CHROME_MIN || isRefreshing) && (
        <div
          className="flex items-center justify-center overflow-hidden flex-shrink-0 bg-white dark:bg-theme-surface"
          style={{
            height: Math.max(pullDistance, isRefreshing ? 76 : 0),
          }}
        >
          <div
            style={{
              opacity:
                pullDistance > PTR_SHOW_CHROME_MIN || isRefreshing
                  ? Math.min(Math.max(pullDistance / PULL_THRESHOLD, isRefreshing ? 1 : 0), 1)
                  : 0,
              transition: 'opacity 0.15s ease-out',
            }}
          >
            {isRefreshing ? (
              <RefreshCw className="w-6 h-6 text-primary-600 animate-spin" />
            ) : (
              <ArrowDown
                className="w-6 h-6 text-primary-600 transition-transform duration-150"
                style={{
                  transform: `rotate(${pullDistance >= PULL_THRESHOLD ? 180 : 0}deg)`,
                }}
              />
            )}
          </div>
        </div>
      )}

      {/* Hero: header + calendar + welcome in one container; header is in-flow (scrolls away). Modifier classes tint banner (e.g. skipped). */}
      <div
        className={`member-dashboard-hero flex-shrink-0 pt-[max(0.5rem,env(safe-area-inset-top))] px-0${selectedDateGradientClass ? ` ${selectedDateGradientClass}` : ''}`}
      >
        <header className="member-dashboard-inflow-header flex-shrink-0 px-6 pb-2">
          <div className="member-dashboard-inflow-header__row flex items-center justify-between min-h-[3.25rem]">
            <h1 className="m-0 leading-none">
              <BravosWordmark className="h-[1.2rem] w-auto max-h-[1.2rem] block text-paper" />
            </h1>
            {hasWorkoutContext && showHeaderToday && (
              <button
                type="button"
                data-calendar-today
                onClick={() => {
                  calendarRef.current?.goToToday();
                  setSelectedDate(getLocalDateKey());
                }}
                className="px-3 py-1.5 text-xs font-medium text-[#151b27] bg-[var(--paper)] rounded-lg flex-shrink-0"
              >
                Today
              </button>
            )}
          </div>
        </header>

        <div
          ref={calendarWrapperRef}
          className="flex-shrink-0 flex flex-col justify-center"
        >
          <div
            className="overflow-hidden relative"
            style={{ zIndex: 2, borderRadius: 0 }}
          >
            <div className="calendar-header-gradient overflow-hidden" style={{ borderRadius: 0 }}>
              <StickyWorkoutCalendar
                ref={calendarRef}
                sessions={allSessions}
                onDateClick={handleDateClick}
                onDisplayedRangeChange={onDisplayedRangeChange}
                initialViewState={initialCalendarState}
                onCalendarStateChange={persistCalendarState}
                onTodayClick={() => setSelectedDate(getLocalDateKey())}
                selectedDate={selectedDate}
                hideHeaderRow
                weekViewOnly
                sticky={false}
              />
            </div>
          </div>
        </div>

        {/* Workout banner – date + card(s) or empty state */}
        <div className="member-dashboard-banner-section pt-0 pb-0">
        {compactLayout ? (
          <div className="welcome-workout-banner pb-0">
            <div className="member-dashboard-workout-banner text-center relative flex flex-col" style={bannerCtaMinWidth != null ? { ['--banner-cta-min-width' as string]: `${bannerCtaMinWidth}px` } : undefined}>
            <div className="relative z-10 flex flex-col">
            {hasWorkoutContext ? (
              selectedDateSessions.length === 0 ? (
                <div className="flex flex-col w-full px-6 sm:px-9 lg:px-12 mt-3 pb-2.5 relative">
                  <div
                    ref={bannerCtaRulerContainerRef}
                    className="member-dashboard-banner-three-section rounded-2xl border-0 border-transparent flex flex-col text-left"
                    style={{ position: 'absolute', left: 0, right: 0, top: 0, visibility: 'hidden', pointerEvents: 'none', height: 48, minWidth: 260 }}
                    aria-hidden
                  >
                    <span ref={bannerCtaRulerRef} className="inline-block text-xs font-normal rounded-full px-3 py-1.5 member-dashboard-banner-cta bg-primary-400 text-gray-800 dark:text-gray-900 whitespace-nowrap">Start Workout</span>
                  </div>
                  <div className="member-dashboard-banner-content-width w-full">
                    <div className="member-dashboard-banner-single-aspect w-full">
                  <div
                    className="member-dashboard-banner-three-section member-dashboard-banner-single-fill rounded-2xl flex flex-col text-left transition-colors relative"
                  >
                    <div
                      className="member-dashboard-banner-image-strip flex-1 min-h-0 w-full rounded-t-2xl"
                      style={{
                        backgroundImage: `url('https://images.unsplash.com/photo-1534796636912-3b95b3ab5986?w=800&q=80')`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                      }}
                      aria-hidden
                    />
                    <div className="member-dashboard-banner-gradient-overlay member-dashboard-banner-gradient-overlay--bottom" aria-hidden />
                    <div className="member-dashboard-banner-top-content member-dashboard-banner-text-shadow">
                      <p className="member-dashboard-banner-subtitle text-white font-normal mb-0 text-[0.8125rem]">{formatSelectedDateLabel(selectedDate)}</p>
                    </div>
                    <div className="member-dashboard-banner-bottom-bar" aria-hidden />
                    <div className="member-dashboard-banner-bottom-content member-dashboard-banner-text-shadow">
                      <div className="flex items-center gap-2 text-white">
                        <RestDayIcon className="w-3.5 h-3.5 flex-shrink-0 fill-current" />
                        <span className="text-xs uppercase tracking-wide">No workout scheduled</span>
                      </div>
                      <p className="font-heading font-bold text-white text-base mt-0.5 leading-tight">Enjoy your rest day!</p>
                      <div className="mt-4 min-h-[1.5rem] shrink-0" aria-hidden />
                    </div>
                  </div>
                    </div>
                  </div>
                </div>
              ) : selectedDateSessions.length === 1 ? (
                (() => {
                  const session = selectedDateSessions[0];
                  const previewUrl = getSessionWorkoutBannerUrl(session);
                  return (
                    <div className="flex flex-col w-full px-6 sm:px-9 lg:px-12 mt-3 pb-2.5 relative">
                      <div
                        ref={bannerCtaRulerContainerRef}
                        className="member-dashboard-banner-three-section rounded-2xl border-0 border-transparent flex flex-col text-left"
                        style={{ position: 'absolute', left: 0, right: 0, top: 0, visibility: 'hidden', pointerEvents: 'none', height: 48, minWidth: 260 }}
                        aria-hidden
                      >
                        <span ref={bannerCtaRulerRef} className="inline-block text-xs font-normal rounded-full px-3 py-1.5 member-dashboard-banner-cta bg-primary-400 text-gray-800 dark:text-gray-900 whitespace-nowrap">Start Workout</span>
                      </div>
                      <div className="member-dashboard-banner-content-width w-full">
                        <div className="member-dashboard-banner-single-aspect w-full">
                      <div
                        data-member-dashboard-workout-banner
                        data-workout-session-id={session.id}
                        className="member-dashboard-banner-three-section member-dashboard-banner-single-fill rounded-2xl flex flex-col text-left transition-colors relative"
                      >
                        <div
                          className="member-dashboard-banner-image-strip flex-1 min-h-0 w-full rounded-t-2xl"
                          style={
                            previewUrl
                              ? {
                                  backgroundImage: `url(${previewUrl})`,
                                  backgroundSize: 'cover',
                                  backgroundPosition: 'center',
                                  backgroundRepeat: 'no-repeat',
                                }
                              : { backgroundColor: '#374151' }
                          }
                          aria-hidden
                        />
                        <div className="member-dashboard-banner-gradient-overlay member-dashboard-banner-gradient-overlay--bottom" aria-hidden />
                        <div className="member-dashboard-banner-top-content member-dashboard-banner-text-shadow">
                          <p className="member-dashboard-banner-subtitle text-white font-normal mb-0 text-[0.8125rem]">{formatSelectedDateLabel(selectedDate)}</p>
                        </div>
                        <div className="member-dashboard-banner-bottom-bar" aria-hidden />
                        <div className="member-dashboard-banner-bottom-content member-dashboard-banner-text-shadow">
                          <div className="flex items-center gap-2 text-white">
                            {getWorkoutStatusInlineIcon(session.status, true)}
                            <span className="text-xs uppercase tracking-wide">
                              {`WORKOUT ${session.status.replace('_', ' ').toUpperCase()}`}
                            </span>
                          </div>
                          <p className="font-heading font-bold text-white text-base mt-0.5 leading-tight min-w-0">
                            {getWorkoutDisplayName(session)}
                          </p>
                          <span className={getWorkoutButtonClass(session.status)}>
                            {getWorkoutActionLabel(session.status)}
                          </span>
                        </div>
                      </div>
                        </div>
                      </div>
                    </div>
                  );
                })()
              ) : (
                <div className="flex flex-col w-full px-6 sm:px-9 lg:px-12 mt-3 pb-2.5">
                  <div className="member-dashboard-banner-content-width w-full">
                  <div
                    ref={carouselScrollRef}
                    className="member-dashboard-workout-carousel overflow-x-auto scrollbar-hide scroll-smooth-touch -mx-6 pl-6 pr-6"
                    style={{ touchAction: 'pan-x pan-y' }}
                  >
                    <div className="flex items-center gap-3 pl-0 pr-4 min-w-min">
                      {selectedDateSessions.map((session) => {
                        const previewUrl = getSessionWorkoutBannerUrl(session);
                        return (
                          <div
                            key={session.id}
                            data-member-dashboard-workout-banner
                            data-workout-session-id={session.id}
                            className="member-dashboard-banner-three-section member-dashboard-banner-carousel-size flex-shrink-0 rounded-2xl flex flex-col no-underline bg-black text-left transition-colors relative"
                            style={{ width: 'min(280px, 70vw)', touchAction: 'pan-x pan-y' }}
                          >
                            <div
                              className="member-dashboard-banner-image-strip flex-1 min-h-0 w-full rounded-t-2xl"
                              style={
                                previewUrl
                                  ? {
                                      backgroundImage: `url(${previewUrl})`,
                                      backgroundSize: 'cover',
                                      backgroundPosition: 'center',
                                      backgroundRepeat: 'no-repeat',
                                      touchAction: 'pan-x pan-y',
                                    }
                                  : { backgroundColor: '#374151', touchAction: 'pan-x pan-y' }
                              }
                              aria-hidden
                            />
                            <div className="member-dashboard-banner-gradient-overlay member-dashboard-banner-gradient-overlay--bottom" aria-hidden />
                            <div className="member-dashboard-banner-top-content member-dashboard-banner-text-shadow">
                              <p className="member-dashboard-banner-subtitle text-white font-normal mb-0 text-[0.8125rem]">{formatSelectedDateLabel(selectedDate)}</p>
                            </div>
                            <div className="member-dashboard-banner-bottom-bar" aria-hidden />
                            <div className="member-dashboard-banner-bottom-content member-dashboard-banner-text-shadow">
                              <div className="flex items-center gap-2 text-white">
                                {getWorkoutStatusInlineIcon(session.status, true)}
                                <span className="text-xs uppercase tracking-wide">
                                  {`WORKOUT ${session.status.replace('_', ' ').toUpperCase()}`}
                                </span>
                              </div>
                              <p className="font-heading font-bold text-white text-base mt-0.5 leading-tight min-w-0">
                                {getWorkoutDisplayName(session)}
                              </p>
                              <span className={getWorkoutButtonClass(session.status)}>
                                {getWorkoutActionLabel(session.status)}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  </div>
                </div>
              )
            ) : (
              <div className="flex flex-col items-center justify-center min-h-[240px] py-8 px-6 sm:px-9 lg:px-12">
                <p className="text-theme-text dark:text-theme-off-white text-base sm:text-lg font-semibold mb-4">You don&apos;t have any active workout plans. Let&apos;s get you started!</p>
                <div className="flex flex-wrap gap-3 justify-center">
                  <Link to="/personal-trainers" className="inline-block px-5 py-2.5 bg-theme-bg-dark text-white rounded-xl font-semibold hover:opacity-90 transition-opacity text-center">
                    Find a Trainer
                  </Link>
                  <Link to="/workout-plans" className="inline-block px-5 py-2.5 bg-white text-theme-bg-dark rounded-xl font-semibold hover:bg-white/90 transition-colors text-center">
                    Browse Plans
                  </Link>
                </div>
              </div>
            )}
            </div>
            </div>
          </div>
        ) : (
          <>
            <div className="member-dashboard-workout-banner text-center relative flex flex-col" style={bannerCtaMinWidth != null ? { ['--banner-cta-min-width' as string]: `${bannerCtaMinWidth}px` } : undefined}>
            <div className="relative z-10 flex flex-col">
            {hasWorkoutContext ? (
              selectedDateSessions.length === 0 ? (
                <div className="flex flex-col w-full px-6 sm:px-9 lg:px-12 mt-3 pb-2.5 relative">
                  <div
                    ref={bannerCtaRulerContainerRef}
                    className="member-dashboard-banner-three-section rounded-2xl border-0 border-transparent flex flex-col text-left"
                    style={{ position: 'absolute', left: 0, right: 0, top: 0, visibility: 'hidden', pointerEvents: 'none', height: 48, minWidth: 260 }}
                    aria-hidden
                  >
                    <span ref={bannerCtaRulerRef} className="inline-block text-xs font-normal rounded-full px-3 py-1.5 member-dashboard-banner-cta bg-primary-400 text-gray-800 dark:text-gray-900 whitespace-nowrap">Start Workout</span>
                  </div>
                  <div className="member-dashboard-banner-content-width w-full">
                    <div className="member-dashboard-banner-single-aspect w-full">
                  <div
                    className="member-dashboard-banner-three-section member-dashboard-banner-single-fill rounded-2xl flex flex-col text-left transition-colors relative"
                  >
                    <div
                      className="member-dashboard-banner-image-strip flex-1 min-h-0 w-full rounded-t-2xl"
                      style={{
                        backgroundImage: `url('https://images.unsplash.com/photo-1534796636912-3b95b3ab5986?w=800&q=80')`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                      }}
                      aria-hidden
                    />
                    <div className="member-dashboard-banner-gradient-overlay member-dashboard-banner-gradient-overlay--bottom" aria-hidden />
                    <div className="member-dashboard-banner-top-content member-dashboard-banner-text-shadow">
                      <p className="member-dashboard-banner-subtitle text-white font-normal mb-0 text-[0.8125rem]">{formatSelectedDateLabel(selectedDate)}</p>
                    </div>
                    <div className="member-dashboard-banner-bottom-bar" aria-hidden />
                    <div className="member-dashboard-banner-bottom-content member-dashboard-banner-text-shadow">
                      <div className="flex items-center gap-2 text-white">
                        <RestDayIcon className="w-3.5 h-3.5 flex-shrink-0 fill-current" />
                        <span className="text-xs uppercase tracking-wide">No workout scheduled</span>
                      </div>
                      <p className="font-heading font-bold text-white text-base mt-0.5 leading-tight">Enjoy your rest day!</p>
                      <div className="mt-4 min-h-[1.5rem] shrink-0" aria-hidden />
                    </div>
                  </div>
                    </div>
                  </div>
                </div>
              ) : selectedDateSessions.length === 1 ? (
                (() => {
                  const session = selectedDateSessions[0];
                  const previewUrl = getSessionWorkoutBannerUrl(session);
                  return (
                    <div className="flex flex-col w-full px-6 sm:px-9 lg:px-12 mt-3 pb-2.5 relative">
                      <div
                        ref={bannerCtaRulerContainerRef}
                        className="member-dashboard-banner-three-section rounded-2xl border-0 border-transparent flex flex-col text-left"
                        style={{ position: 'absolute', left: 0, right: 0, top: 0, visibility: 'hidden', pointerEvents: 'none', height: 48, minWidth: 260 }}
                        aria-hidden
                      >
                        <span ref={bannerCtaRulerRef} className="inline-block text-xs font-normal rounded-full px-3 py-1.5 member-dashboard-banner-cta bg-primary-400 text-gray-800 dark:text-gray-900 whitespace-nowrap">Start Workout</span>
                      </div>
                      <div className="member-dashboard-banner-content-width w-full">
                        <div className="member-dashboard-banner-single-aspect w-full">
                      <div
                        data-member-dashboard-workout-banner
                        data-workout-session-id={session.id}
                        className="member-dashboard-banner-three-section member-dashboard-banner-single-fill rounded-2xl flex flex-col text-left transition-colors relative"
                      >
                        <div
                          className="member-dashboard-banner-image-strip flex-1 min-h-0 w-full rounded-t-2xl"
                          style={
                            previewUrl
                              ? {
                                  backgroundImage: `url(${previewUrl})`,
                                  backgroundSize: 'cover',
                                  backgroundPosition: 'center',
                                  backgroundRepeat: 'no-repeat',
                                }
                              : { backgroundColor: '#374151' }
                          }
                          aria-hidden
                        />
                        <div className="member-dashboard-banner-gradient-overlay member-dashboard-banner-gradient-overlay--bottom" aria-hidden />
                        <div className="member-dashboard-banner-top-content member-dashboard-banner-text-shadow">
                          <p className="member-dashboard-banner-subtitle text-white font-normal mb-0 text-[0.8125rem]">{formatSelectedDateLabel(selectedDate)}</p>
                        </div>
                        <div className="member-dashboard-banner-bottom-bar" aria-hidden />
                        <div className="member-dashboard-banner-bottom-content member-dashboard-banner-text-shadow">
                          <div className="flex items-center gap-2 text-white">
                            {getWorkoutStatusInlineIcon(session.status, true)}
                            <span className="text-xs uppercase tracking-wide">
                              {`WORKOUT ${session.status.replace('_', ' ').toUpperCase()}`}
                            </span>
                          </div>
                          <p className="font-heading font-bold text-white text-base mt-0.5 leading-tight min-w-0">
                            {getWorkoutDisplayName(session)}
                          </p>
                          <span className={getWorkoutButtonClass(session.status)}>
                            {getWorkoutActionLabel(session.status)}
                          </span>
                        </div>
                      </div>
                        </div>
                      </div>
                    </div>
                  );
                })()
              ) : (
                <div className="flex flex-col w-full px-6 sm:px-9 lg:px-12 mt-3 pb-2.5">
                  <div className="member-dashboard-banner-content-width w-full">
                    <div
                      ref={carouselScrollRef}
                      className="member-dashboard-workout-carousel overflow-x-auto scrollbar-hide scroll-smooth-touch -mx-6 pl-6 pr-6"
                      style={{ touchAction: 'pan-x pan-y' }}
                    >
                      <div className="flex items-center gap-3 pl-0 pr-4 min-w-min">
                        {selectedDateSessions.map((session) => {
                          const previewUrl = getSessionWorkoutBannerUrl(session);
                          return (
                            <div
                              key={session.id}
                              data-member-dashboard-workout-banner
                              data-workout-session-id={session.id}
                              className="member-dashboard-banner-three-section member-dashboard-banner-carousel-size flex-shrink-0 rounded-2xl flex flex-col no-underline bg-black text-left transition-colors relative"
                              style={{ width: 'min(280px, 70vw)', touchAction: 'pan-x pan-y' }}
                            >
                              <div
                                className="member-dashboard-banner-image-strip flex-1 min-h-0 w-full rounded-t-2xl"
                                style={
                                  previewUrl
                                    ? {
                                        backgroundImage: `url(${previewUrl})`,
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                        backgroundRepeat: 'no-repeat',
                                        touchAction: 'pan-x pan-y',
                                      }
                                    : { backgroundColor: '#374151', touchAction: 'pan-x pan-y' }
                                }
                                aria-hidden
                              />
                              <div className="member-dashboard-banner-gradient-overlay member-dashboard-banner-gradient-overlay--bottom" aria-hidden />
                              <div className="member-dashboard-banner-top-content member-dashboard-banner-text-shadow">
                                <p className="member-dashboard-banner-subtitle text-white font-normal mb-0 text-[0.8125rem]">{formatSelectedDateLabel(selectedDate)}</p>
                              </div>
                              <div className="member-dashboard-banner-bottom-bar" aria-hidden />
                              <div className="member-dashboard-banner-bottom-content member-dashboard-banner-text-shadow">
                                <div className="flex items-center gap-2 text-white">
                                  {getWorkoutStatusInlineIcon(session.status, true)}
                                  <span className="text-xs uppercase tracking-wide">
                                    {`WORKOUT ${session.status.replace('_', ' ').toUpperCase()}`}
                                  </span>
                                </div>
                                <p className="font-heading font-bold text-white text-base mt-0.5 leading-tight min-w-0">
                                  {getWorkoutDisplayName(session)}
                                </p>
                                <span className={getWorkoutButtonClass(session.status)}>
                                  {getWorkoutActionLabel(session.status)}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )
            ) : (
              <div className="flex flex-col items-center justify-center min-h-[240px] py-8 px-6 sm:px-9 lg:px-12">
                <p className="text-theme-text/80 dark:text-theme-off-white/80 text-base sm:text-lg font-semibold mb-4">You don&apos;t have any active workout plans. Let&apos;s get you started!</p>
                <div className="flex flex-wrap gap-3 justify-center">
                  <Link to="/personal-trainers" className="inline-block px-5 py-2.5 bg-theme-bg-dark/10 dark:bg-white/10 backdrop-blur-sm text-theme-text dark:text-white rounded-xl font-semibold hover:bg-theme-bg-dark/20 dark:hover:bg-white/20 transition-colors text-center border border-theme-bg-dark/20 dark:border-white/20">
                    Find a Trainer
                  </Link>
                  <Link to="/workout-plans" className="inline-block px-5 py-2.5 bg-theme-bg-dark dark:bg-white text-white dark:text-theme-bg-dark rounded-xl font-semibold hover:opacity-90 transition-colors text-center">
                    Browse Plans
                  </Link>
                </div>
              </div>
            )}
            </div>
            </div>
          </>
        )}
        </div>
      </div>

      {/* iOS parity: fixed green → page bridge; heading vertically centered when plans exist */}
      <div
        className={`member-dashboard-plans-bridge${sortedMemberPlans.length > 0 ? ' member-dashboard-plans-bridge--with-heading' : ''}`}
      >
        {sortedMemberPlans.length > 0 && (
          <h2 className="member-dashboard-plans-bridge__heading text-xl sm:text-2xl font-heading font-semibold text-theme-text dark:text-theme-off-white">
            {sortedMemberPlans.length === 1 ? 'Your Workout Plan' : 'Your Workout Plans'}
          </h2>
        )}
      </div>

      <div className="member-dashboard-gradient-section">
      <div className="member-dashboard-content-inner relative z-10 px-6 sm:px-9 lg:px-12 pt-0 pb-6">
        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-gray-100 dark:bg-white/10 rounded-2xl border border-theme-border">
            <p className="text-sm text-theme-text dark:text-theme-off-white">{error}</p>
          </div>
        )}

        {/* Progress Summary – one card per assigned plan (same UI, scoped sessions) */}
        {sortedMemberPlans.map((plan) => {
          const bannerStats = planBannerStatsByPlanId[plan.id];
          const completed = bannerStats?.completed ?? 0;
          // Denominator = current scheduled rows for this assignment (updates when trainer deletes a session).
          const totalWorkouts =
            bannerStats !== undefined
              ? bannerStats.total
              : plan.workoutPlan.totalNoOfDays || 0;
          // Plan length in weeks comes from template calendar (totalNoOfDays), not session count / 7.
          const planDays = plan.workoutPlan.totalNoOfDays || 0;
          const weeksTotal =
            planDays > 0
              ? Math.ceil(planDays / 7)
              : totalWorkouts > 0
                ? Math.ceil(totalWorkouts / 7)
                : 0;
          const workoutProgressPct =
            totalWorkouts > 0 ? Math.min(100, (completed / totalWorkouts) * 100) : 0;
          const planWeeksLabel =
            weeksTotal > 0
              ? `${weeksTotal} week${weeksTotal !== 1 ? 's' : ''}`
              : '—';
          const statusLabel =
            plan.status === 'active'
              ? 'Active'
              : plan.status === 'paused'
                ? 'Paused'
                : plan.status === 'completed'
                  ? 'Completed'
                  : 'Cancelled';
          return (
            <div key={plan.id} className="member-dashboard-plan-card-wrap relative z-10">
              <div className="member-dashboard-plan-card relative overflow-hidden rounded-3xl p-6 sm:p-8">
                <div className="flex flex-wrap items-start justify-between gap-2 mb-5">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-lg sm:text-xl font-heading font-semibold text-theme-text dark:text-theme-off-white">
                      {plan.workoutPlan.name}
                    </h3>
                    <p className="mt-1.5 text-xs sm:text-sm font-medium uppercase tracking-wide text-theme-text/70 dark:text-theme-off-white/70">
                      {planWeeksLabel}
                    </p>
                  </div>
                  <span className="text-xs font-medium uppercase tracking-wide px-2.5 py-1 rounded-full bg-theme-border/60 dark:bg-white/10 text-theme-text/80 dark:text-theme-off-white/80 shrink-0">
                    {statusLabel}
                  </span>
                </div>
                <div className="space-y-4">
                  <div className="w-full bg-theme-border dark:bg-white/10 rounded-full h-2.5 sm:h-3">
                    <div
                      className="bg-primary-600 h-2.5 sm:h-3 rounded-full transition-all"
                      style={{ width: `${workoutProgressPct}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-sm sm:text-base gap-2">
                    <span className="text-theme-text/80 dark:text-theme-off-white/80">Total Workouts</span>
                    <span className="font-semibold text-theme-text dark:text-theme-off-white text-right shrink-0">
                      {completed} / {totalWorkouts} completed
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      </div>

      </main>
      </div>
    </div>
  );
}; 
